import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchSyncRunExport,
  type SyncConfigPatch,
  type SyncConfigRow,
  useSyncConfigMutations,
  useSyncConfigs,
  useSyncStrategies,
  useTenants,
} from '../hooks/api';
import { ErrorBox, Loading, EmptyBox } from '../components/Loading';
import { Status, statusKindForJobStatus } from '../components/Status';
import { ApiError, formatNumber, formatTimestamp } from '../api';

export default function SyncControlPage() {
  const tenants = useTenants();
  const [filter, setFilter] = useState('');
  const visibleTenants = useMemo(
    () =>
      tenants.data?.data.filter((t) =>
        filter ? t.code.toLowerCase().includes(filter.toLowerCase()) : true,
      ) ?? [],
    [filter, tenants.data?.data],
  );
  const strategies = useSyncStrategies(visibleTenants[0]?.code);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border-b border-neutral-300 pb-1">
        <h1 className="text-[14px] font-semibold uppercase tracking-wide text-neutral-700">
          Sync-Steuerung
        </h1>
        <Link className="btn" to="/operations/sync-logs">
          Sync-Logs
        </Link>
      </div>

      <div className="panel">
        <div className="panel-h">Sync-Typen</div>
        <div className="panel-b p-0">
          {strategies.isLoading && (
            <div className="p-2">
              <Loading />
            </div>
          )}
          {strategies.error && (
            <div className="p-2">
              <ErrorBox error={strategies.error} />
            </div>
          )}
          {strategies.data && (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>Beschreibung</th>
                  <th>Geeignet fuer</th>
                  <th>API-Last</th>
                  <th>Cursor</th>
                  <th className="num">Min.</th>
                </tr>
              </thead>
              <tbody>
                {strategies.data.data.map((s) => (
                  <tr key={s.code}>
                    <td>
                      <div className="mono">{s.code}</div>
                      <div className="text-[11px] text-neutral-600">
                        {s.label}
                      </div>
                    </td>
                    <td className="max-w-[320px]">{s.description}</td>
                    <td className="max-w-[260px]">{s.best_for}</td>
                    <td className="max-w-[260px]">
                      <Status kind={s.large_table_safe ? 'ok' : 'warn'}>
                        {s.large_table_safe ? 'gross sicher' : 'vorsicht'}
                      </Status>
                      <div className="mt-1 text-[11px] text-neutral-700">
                        {s.api_load_notes}
                      </div>
                    </td>
                    <td className="max-w-[240px]">{s.cursor_description}</td>
                    <td className="num">
                      {formatMinutes(s.default_min_frequency_minutes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-h">Filter</div>
        <div className="panel-b">
          <input
            type="text"
            className="input w-full"
            placeholder="Tenant-Code..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {tenants.isLoading && <Loading />}
      {tenants.error && <ErrorBox error={tenants.error} />}
      {visibleTenants.map((t) => (
        <TenantSyncControlBlock
          key={t.code}
          tenantCode={t.code}
          tenantName={t.name}
        />
      ))}
      {tenants.data?.data.length === 0 && <EmptyBox>Keine Tenants.</EmptyBox>}
    </div>
  );
}

function TenantSyncControlBlock({
  tenantCode,
  tenantName,
}: {
  tenantCode: string;
  tenantName: string;
}) {
  const configs = useSyncConfigs(tenantCode);
  const strategies = useSyncStrategies(tenantCode);

  return (
    <div className="panel">
      <div className="panel-h flex items-center justify-between">
        <span>
          {tenantCode}{' '}
          <span className="font-normal text-neutral-500">- {tenantName}</span>
        </span>
        <Link
          to={`/tenants/${encodeURIComponent(tenantCode)}`}
          className="text-[11px] underline"
        >
          Tenant-Detail
        </Link>
        <Link
          to={syncLogsHref(tenantCode)}
          className="ml-3 text-[11px] underline"
        >
          Sync-Logs
        </Link>
      </div>
      <div className="panel-b p-0">
        {configs.isLoading && (
          <div className="p-2">
            <Loading />
          </div>
        )}
        {configs.error && (
          <div className="p-2">
            <ErrorBox error={configs.error} />
          </div>
        )}
        {configs.data && configs.data.data.length === 0 && (
          <div className="p-2">
            <EmptyBox>Keine Sync-Konfigurationen.</EmptyBox>
          </div>
        )}
        {configs.data && configs.data.data.length > 0 && (
          <div className="overflow-hidden">
            <table className="tbl table-fixed text-[11.5px]">
              <colgroup>
                <col className="w-[3%]" />
                <col className="w-[23%]" />
                <col className="w-[15%]" />
                <col className="w-[7%]" />
                <col className="w-[17%]" />
                <col className="w-[13%]" />
                <col className="w-[11%]" />
                <col className="w-[5%]" />
                <col className="w-[6%]" />
              </colgroup>
              <thead>
                <tr>
                  <th>Aktiv</th>
                  <th>Sync</th>
                  <th>Typ</th>
                  <th className="num">Takt</th>
                  <th>Limits</th>
                  <th>Status</th>
                  <th>Laeufe</th>
                  <th>Logs</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {configs.data.data.map((row) => (
                  <SyncConfigEditor
                    key={row.id}
                    tenantCode={tenantCode}
                    row={row}
                    strategies={strategies.data?.data.map((s) => s.code) ?? []}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SyncConfigEditor({
  tenantCode,
  row,
  strategies,
}: {
  tenantCode: string;
  row: SyncConfigRow;
  strategies: string[];
}) {
  const mut = useSyncConfigMutations(tenantCode);
  const [draft, setDraft] = useState<Required<SyncConfigPatch>>(() =>
    toDraft(row),
  );

  useEffect(() => {
    setDraft(toDraft(row));
  }, [row]);

  const implemented = row.implemented === true;
  const draftStrategyExecutable = isStrategyExecutable(row, draft.strategy);
  const selectableStrategies = strategyOptionsForRow(row, strategies, draft.strategy);
  const canToggleActive = implemented;
  const busy = mut.updateConfig.isPending || mut.runNow.isPending;
  const error = mut.updateConfig.error ?? mut.runNow.error;
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const save = () => {
    mut.updateConfig.mutate({ configId: row.id, patch: draft });
  };

  async function exportLastRunTxt() {
    if (!row.last_run) return;
    setExporting(true);
    setExportError(null);
    try {
      const text = await fetchSyncRunExport(tenantCode, row.last_run.id);
      downloadText(
        text,
        `sync-run-${safeFilePart(tenantCode)}-${safeFilePart(row.source_system)}-${safeFilePart(row.entity)}-${row.last_run.id}.txt`,
      );
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }

  return (
    <tr>
      <td className="text-center">
        <input
          type="checkbox"
          checked={draft.enabled}
          disabled={busy || !canToggleActive}
          onChange={(e) =>
            setDraft((d) => ({ ...d, enabled: e.target.checked }))
          }
          title={activationTitle(row, draft.strategy)}
        />
      </td>
      <td>
        <div className="flex items-center gap-2">
          <span className="mono truncate" title={row.entity}>
            {row.entity}
          </span>
          <Status kind={row.environment === 'live' ? 'ok' : 'info'}>
            {row.environment}
          </Status>
        </div>
        <div className="truncate text-[10.5px] text-neutral-600">
          {row.source_schema && row.source_table ? (
            <Link
              className="underline"
              to={`/tenants/${encodeURIComponent(tenantCode)}/db/${encodeURIComponent(row.source_schema)}/${encodeURIComponent(row.source_table)}`}
              title={`${row.source_schema}.${row.source_table}`}
            >
              {row.source_schema}.{row.source_table}
            </Link>
          ) : (
            '-'
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-[10.5px] text-neutral-600">
          <span>
            {row.source_system}/{row.integration_code}
          </span>
          {row.large_table && <Status kind="warn">gross</Status>}
        </div>
      </td>
      <td>
        <select
          className="input w-full truncate"
          value={draft.strategy}
          disabled={busy}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              strategy: e.target.value,
              enabled: false,
            }))
          }
        >
          {selectableStrategies.map((s) => (
            <option key={s} value={s}>
              {isStrategyExecutable(row, s) ? s : `${s} (nicht bereit)`}
            </option>
          ))}
        </select>
        {row.default_strategy && (
          <div className="truncate text-[10.5px] text-neutral-600" title={`Default: ${row.default_strategy}`}>
            Default: {row.default_strategy}
          </div>
        )}
        <div
          className={`text-[10.5px] ${
            draftStrategyExecutable ? 'text-neutral-600' : 'text-st-warn'
          }`}
        >
          Worker: {draftStrategyExecutable ? 'bereit' : 'noch nicht bereit'}
        </div>
      </td>
      <td className="num">
        <NumberInput
          value={draft.frequency_minutes}
          min={5}
          max={10080}
          className="w-full max-w-[70px]"
          disabled={busy}
          onChange={(v) => setDraft((d) => ({ ...d, frequency_minutes: v }))}
        />
        <div className="text-[10.5px] text-neutral-600">
          {formatMinutes(draft.frequency_minutes)}
        </div>
      </td>
      <td>
        <div className="grid grid-cols-3 gap-1">
          <label className="block text-[10.5px] text-neutral-600">
            Page
            <NumberInput
              value={draft.page_size}
              min={1}
              max={1000}
              className="mt-0.5 w-full"
              disabled={busy}
              onChange={(v) => setDraft((d) => ({ ...d, page_size: v }))}
            />
          </label>
          <label className="block text-[10.5px] text-neutral-600">
            Req
            <NumberInput
              value={draft.max_requests_per_minute}
              min={1}
              max={600}
              className="mt-0.5 w-full"
              disabled={busy}
              onChange={(v) =>
                setDraft((d) => ({ ...d, max_requests_per_minute: v }))
              }
            />
          </label>
          <label className="block text-[10.5px] text-neutral-600">
            Tage
            <NumberInput
              value={draft.rolling_window_days}
              min={1}
              max={365}
              className="mt-0.5 w-full"
              disabled={busy}
              onChange={(v) => setDraft((d) => ({ ...d, rolling_window_days: v }))}
            />
          </label>
        </div>
      </td>
      <td>
        <Status
          kind={
            implemented
              ? row.enabled
                ? 'ok'
                : draftStrategyExecutable
                  ? 'muted'
                  : 'warn'
              : 'warn'
          }
        >
          {implemented
            ? row.enabled
              ? draftStrategyExecutable
                ? 'aktiv'
                : 'aktiv / wartet'
              : draftStrategyExecutable
                ? 'bereit'
                : 'vorbereitet'
            : 'geplant'}
        </Status>
        {error && (
          <div className="mt-1 truncate text-[10.5px] text-st-err" title={error.message}>
            {error.message}
          </div>
        )}
        {row.last_error && (
          <div className="mt-1 truncate text-[10.5px] text-st-err" title={row.last_error}>
            {row.last_error}
          </div>
        )}
        <div className="mt-1 truncate text-[10.5px] text-neutral-600" title={formatCursor(row.cursor)}>
          {formatCursor(row.cursor)}
        </div>
      </td>
      <td>
        <div className="mono text-[10.5px]">
          N: {formatTimestamp(row.next_run_at)}
        </div>
        {row.last_run ? (
          <>
            <Status kind={statusKindForJobStatus(row.last_run.status)}>
              {row.last_run.status}
            </Status>
            <div className="mono mt-1 text-[10.5px]">
              L: {formatTimestamp(row.last_run.started_at)}
            </div>
            <div className="text-[10.5px] text-neutral-600">
              {formatNumber(Number(row.last_run.rows_processed))} Zeilen
            </div>
          </>
        ) : (
          <span className="text-neutral-500">-</span>
        )}
      </td>
      <td>
        <div className="flex flex-col gap-1">
          <Link className="btn px-1 text-center text-[11px]" to={syncLogsHref(tenantCode, row)}>
            Details
          </Link>
          <button
            type="button"
            className="btn px-1 text-[11px]"
            disabled={!row.last_run || exporting}
            onClick={exportLastRunTxt}
            title="Letzten Synclauf als TXT exportieren"
          >
            {exporting ? '...' : 'TXT'}
          </button>
        </div>
        {exportError && (
          <div className="mt-1 truncate text-[10.5px] text-st-err" title={exportError}>
            {exportError}
          </div>
        )}
      </td>
      <td>
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className="btn-primary px-1 text-[11px]"
            disabled={busy}
            onClick={save}
          >
            Speich.
          </button>
          <button
            type="button"
            className="btn px-1 text-[11px]"
            disabled={
              busy ||
              !row.enabled ||
              !implemented ||
              !isStrategyExecutable(row, row.strategy)
            }
            onClick={() => mut.runNow.mutate(row.id)}
          >
            Run
          </button>
        </div>
        {row.implementation_notes && (
          <div
            className="mt-1 truncate text-[10.5px] text-neutral-600"
            title={row.implementation_notes}
          >
            {row.implementation_notes}
          </div>
        )}
      </td>
    </tr>
  );
}

function NumberInput({
  value,
  min,
  max,
  className = 'w-[74px]',
  disabled,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  className?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <input
      type="number"
      className={`input text-right ${className}`}
      min={min}
      max={max}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  );
}

function toDraft(row: SyncConfigRow): Required<SyncConfigPatch> {
  return {
    enabled: row.enabled,
    strategy: row.strategy,
    frequency_minutes: row.frequency_minutes,
    safety_lag_minutes: row.safety_lag_minutes,
    rolling_window_days: row.rolling_window_days,
    page_size: row.page_size,
    max_requests_per_minute: row.max_requests_per_minute,
    cooldown_ms_between_pages: row.cooldown_ms_between_pages,
    max_runtime_seconds: row.max_runtime_seconds,
    max_parallel_runs: row.max_parallel_runs,
    priority: row.priority,
    notes: row.notes,
  };
}

function formatMinutes(value: number): string {
  if (value < 60) return `${value} min`;
  if (value % 1440 === 0) return `${value / 1440} d`;
  if (value % 60 === 0) return `${value / 60} h`;
  return `${value} min`;
}

function isStrategyExecutable(row: SyncConfigRow, strategy: string): boolean {
  return row.implemented === true && row.executable_strategies.includes(strategy);
}

function strategyOptionsForRow(
  row: SyncConfigRow,
  allStrategies: string[],
  currentStrategy: string,
): string[] {
  const executable =
    row.executable_strategies.length > 0 ? row.executable_strategies : allStrategies;
  return executable.includes(currentStrategy) ? executable : [currentStrategy, ...executable];
}

function activationTitle(row: SyncConfigRow, strategy: string): string {
  if (row.implemented !== true) return 'Adapter noch nicht implementiert';
  if (!isStrategyExecutable(row, strategy))
    return 'Im Leitstand aktivierbar; der Worker startet erst, sobald diese Strategie implementiert ist';
  return 'Aktiv';
}

function formatCursor(cursor: Record<string, unknown>): string {
  const mode = cursor['mode'];
  if (
    mode !== 'initial_backfill' &&
    mode !== 'append_created' &&
    mode !== 'delta_modified'
  )
    return '';
  if (mode === 'delta_modified') {
    const lastModifiedAt =
      typeof cursor['last_modified_at'] === 'string'
        ? cursor['last_modified_at']
        : null;
    const lowerBoundAt =
      typeof cursor['lower_bound_at'] === 'string'
        ? cursor['lower_bound_at']
        : null;
    const rows =
      typeof cursor['rows_processed_total'] === 'number'
        ? formatNumber(cursor['rows_processed_total'])
        : String(cursor['rows_processed_total'] ?? '0');
    const last = lastModifiedAt ? ` bis ${formatTimestamp(lastModifiedAt)}` : '';
    const from = lowerBoundAt ? ` ab ${formatTimestamp(lowerBoundAt)}` : '';
    return `Delta${from}${last}: ${rows} Zeilen`;
  }
  const done = cursor['complete'] === true ? 'fertig' : 'laeuft';
  const rows =
    typeof cursor['rows_processed_total'] === 'number'
      ? formatNumber(cursor['rows_processed_total'])
      : String(cursor['rows_processed_total'] ?? '0');
  const lastId = cursor['last_weclapp_id']
    ? ` bis ID ${String(cursor['last_weclapp_id'])}`
    : '';
  if (mode === 'append_created')
    return `Append/Nachziehen ${done}: ${rows} Zeilen${lastId}`;
  return `Backfill ${done}: ${rows} Zeilen${lastId}`;
}

function syncLogsHref(tenantCode: string, row?: SyncConfigRow): string {
  const qs = new URLSearchParams();
  qs.set('tenant', tenantCode);
  if (row) {
    qs.set('source_system', row.source_system);
    qs.set('environment', row.environment);
    qs.set('entity', row.entity);
  }
  return `/operations/sync-logs?${qs.toString()}`;
}

function downloadText(text: string, filename: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function safeFilePart(value: string): string {
  return value.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'sync';
}
